import { Typography, Box, Grid, Stack } from "@mui/material";

import { logo, iphoneFrame } from "../../assets";

import Card from "../Card";
import {
  FacebookIcon,
  XIcon,
  WhatsappIcon,
  BehanceIcon,
  MailIcon,
  InstagramIcon,
  LinkIcon,
  LinkedinIcon,
  PhoneIcon,
  PinterestIcon,
  SpotifyIcon,
  TikTokIcon,
  SnapchatIcon,
  AnghamiIcon,
  AppleIcon,
} from "../icons";

import { profileThemeColors } from "../../config/profileThemeColors";
import { useLocalizedTranslation } from "../../hooks/useLocalizedTranslation";

import "./styles.css";

const Social = ({ theme, Icon, title, number }) => {
  return (
    <div className="social-column">
      <div
        className="social-icon-wrap"
        style={{
          backgroundColor: profileThemeColors[theme].secondary,
        }}
      >
        {number && (
          <div
            className="social-number-wrap"
            style={{
              backgroundColor: profileThemeColors[theme].secondary,
            }}
          >
            <span
              className="social-number"
              style={{
                color: profileThemeColors[theme].primary,
              }}
            >
              {number}
            </span>
          </div>
        )}

        <Icon color={profileThemeColors[theme].primary} size={24} />
      </div>

      <Typography
        fontSize={10}
        mt={1}
        color={profileThemeColors[theme].secondary}
      >
        {title}
      </Typography>
    </div>
  );
};

const ProfilePhonePreview = ({
  profileView = "default",
  name = "",
  bio = "",
  position = "",
  company = "",
  industry = "",
  email = "",
  secondaryEmail = "",
  mobile,
  landlineNumber,
  landlineExtension,
  secondaryMobile,
  address,
  theme,
  profilePhoto,
  socialAccounts = null,
  documents = null,
  language = null,
}) => {
  const { t: translation } = useLocalizedTranslation(language, [
    "components/profilePhonePreview",
    "components/industrySelect",
  ]);

  if (profileView === "default")
    return (
      <div
        className="profile-phone-preview"
        style={{
          backgroundImage: `url(${iphoneFrame})`,
          overflow: "hidden",
          direction: language && language === "ar" ? "rtl" : "ltr",
        }}
      >
        <Grid
          container
          spacing={2}
          pt={6}
          px={1}
          sx={{ overflow: "hidden", height: "100%" }}
          alignItems="flex-start"
        >
          {(profilePhoto?.image || profilePhoto) && (
            <Grid item xs={12}>
              <img
                src={profilePhoto?.image ? profilePhoto.image : profilePhoto}
                alt="profile"
                style={{
                  width: "100%",
                  maxWidth: "64px",
                  borderRadius: "100%",
                  aspectRatio: "1",
                  objectFit: "cover",
                  margin: "0 auto",
                  display: "block",
                }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Card fullWidth style={{ padding: "16px" }}>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <span
                  className="arrowIcon"
                  style={{ flexShrink: 0, width: "20px", height: "20px" }}
                >
                  {">"}
                </span>

                <Typography fontSize={18}>
                  {translation("personalInformationSubtitle")}
                </Typography>
              </Stack>

              <Stack gap={1}>
                {/* Name */}
                {name && (
                  <Box>
                    <Typography fontSize={16} sx={{ color: "#747474" }}>
                      {translation("nameSubtitle")}
                    </Typography>

                    <Typography component="p" variant="body1" fontSize={14}>
                      {name}
                    </Typography>
                  </Box>
                )}

                {/* Company */}
                {company && (
                  <Box>
                    <Typography fontSize={16} sx={{ color: "#747474" }}>
                      {translation("companySubtitle")}
                    </Typography>

                    <Typography component="p" variant="body1" fontSize={14}>
                      {company}
                    </Typography>
                  </Box>
                )}

                {/* Position */}
                {position && (
                  <Box>
                    <Typography fontSize={16} sx={{ color: "#747474" }}>
                      {translation("positionSubtitle")}
                    </Typography>

                    <Typography component="p" variant="body1" fontSize={14}>
                      {position}
                    </Typography>
                  </Box>
                )}

                {/* Industry */}
                {industry && (
                  <Box>
                    <Typography fontSize={16} sx={{ color: "#747474" }}>
                      {translation("industrySubtitle")}
                    </Typography>

                    <Typography component="p" variant="body1" fontSize={14}>
                      {industry}
                    </Typography>
                  </Box>
                )}

                {/* Bio */}
                {bio && (
                  <Box>
                    <Typography fontSize={16} sx={{ color: "#747474" }}>
                      {translation("bioSubtitle")}
                    </Typography>

                    <Typography component="p" variant="body1" fontSize={14}>
                      {bio}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>
          </Grid>

          {(mobile ||
            email ||
            secondaryMobile ||
            secondaryEmail ||
            landlineNumber ||
            address) && (
            <Grid item xs={12}>
              <Card fullWidth style={{ padding: "16px" }}>
                <Stack direction="row" alignItems="center" gap={1} mb={2}>
                  <span
                    className="arrowIcon"
                    style={{ flexShrink: 0, width: "20px", height: "20px" }}
                  >
                    {">"}
                  </span>

                  <Typography fontSize={18}>
                    {translation("contactSubtitle")}
                  </Typography>
                </Stack>

                <Stack gap={1}>
                  {/* Mobile */}
                  {mobile && (
                    <Box>
                      <Typography fontSize={16} sx={{ color: "#747474" }}>
                        {translation("mobileSubtitle")}
                      </Typography>

                      <Typography component="p" variant="body1" fontSize={14}>
                        {mobile}
                      </Typography>
                    </Box>
                  )}

                  {/* Secondary mobile */}
                  {secondaryMobile && (
                    <Box>
                      <Typography fontSize={16} sx={{ color: "#747474" }}>
                        {translation("secondaryMobileSubtitle")}
                      </Typography>

                      <Typography component="p" variant="body1" fontSize={14}>
                        {secondaryMobile}
                      </Typography>
                    </Box>
                  )}

                  {/* Landline number */}
                  {landlineNumber && (
                    <Box>
                      <Typography fontSize={16} sx={{ color: "#747474" }}>
                        {translation("landlineNumberSubtitle")}
                      </Typography>

                      <Typography component="p" variant="body1" fontSize={14}>
                        {landlineNumber}
                        {landlineExtension &&
                          ` ${translation(
                            "landlineExtensionText"
                          )} ${landlineExtension}`}
                      </Typography>
                    </Box>
                  )}

                  {/* Email */}
                  {email && (
                    <Box>
                      <Typography fontSize={16} sx={{ color: "#747474" }}>
                        {translation("emailSubtitle")}
                      </Typography>

                      <Typography component="p" variant="body1" fontSize={14}>
                        {email}
                      </Typography>
                    </Box>
                  )}

                  {/* Secondary email */}
                  {secondaryEmail && (
                    <Box>
                      <Typography fontSize={16} sx={{ color: "#747474" }}>
                        {translation("secondaryEmailSubtitle")}
                      </Typography>

                      <Typography component="p" variant="body1" fontSize={14}>
                        {secondaryEmail}
                      </Typography>
                    </Box>
                  )}

                  {/* Address */}
                  {address && (
                    <Box>
                      <Typography fontSize={16} sx={{ color: "#747474" }}>
                        {translation("addressSubtitle")}
                      </Typography>

                      <Typography component="p" variant="body1" fontSize={14}>
                        {address}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Card>
            </Grid>
          )}

          {(socialAccounts?.link ||
            socialAccounts?.whatsapp ||
            socialAccounts?.facebook ||
            socialAccounts?.instagram ||
            socialAccounts?.linkedin ||
            socialAccounts?.pinterest ||
            socialAccounts?.x ||
            socialAccounts?.behance ||
            socialAccounts?.tikTok ||
            socialAccounts?.snapchat ||
            socialAccounts?.spotify ||
            socialAccounts?.apple ||
            socialAccounts?.anghami) && (
            <Grid item xs={12}>
              <Card fullWidth style={{ padding: "16px" }}>
                <Stack direction="row" alignItems="center" gap={1} mb={2}>
                  <span
                    className="arrowIcon"
                    style={{ flexShrink: 0, width: "20px", height: "20px" }}
                  >
                    {">"}
                  </span>

                  <Typography fontSize={18}>
                    {translation("socialLinksSubtitle")}
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  {socialAccounts?.link &&
                    Array.isArray(socialAccounts.link) && (
                      <Box>
                        <Typography
                          component="h2"
                          variant="subtitle1"
                          sx={{ color: "#747474" }}
                          mb={1}
                        >
                          {translation("customLinksText")}
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          flexWrap="wrap"
                        >
                          {socialAccounts.link.map((account, index) => (
                            <LinkIcon key={index} />
                          ))}
                        </Stack>
                      </Box>
                    )}

                  {socialAccounts?.whatsapp &&
                    Array.isArray(socialAccounts.whatsapp) && (
                      <Box>
                        <Typography
                          component="h2"
                          variant="subtitle1"
                          sx={{ color: "#747474" }}
                          mb={1}
                        >
                          {translation("contactInfoText")}
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ gap: "8px", "& .MuiStack-root": { margin: 0 } }}
                        >
                          {socialAccounts.whatsapp.map((account, index) => (
                            <WhatsappIcon key={index} />
                          ))}
                        </Stack>
                      </Box>
                    )}

                  {(socialAccounts?.facebook ||
                    socialAccounts?.instagram ||
                    socialAccounts?.linkedin ||
                    socialAccounts?.pinterest ||
                    socialAccounts?.x ||
                    socialAccounts?.behance ||
                    socialAccounts?.tikTok ||
                    socialAccounts?.snapchat) && (
                    <Box>
                      <Typography
                        component="h2"
                        variant="subtitle1"
                        sx={{ color: "#747474" }}
                        mb={1}
                      >
                        {translation("socialMediaText")}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ gap: "8px", "& .MuiStack-root": { margin: 0 } }}
                      >
                        {socialAccounts?.facebook &&
                          Array.isArray(socialAccounts.facebook) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.facebook.map((account, index) => (
                                <FacebookIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.instagram &&
                          Array.isArray(socialAccounts.instagram) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.instagram.map(
                                (account, index) => (
                                  <InstagramIcon key={index} />
                                )
                              )}
                            </Stack>
                          )}

                        {socialAccounts?.linkedin &&
                          Array.isArray(socialAccounts.linkedin) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.linkedin.map((account, index) => (
                                <LinkedinIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.pinterest &&
                          Array.isArray(socialAccounts.pinterest) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.pinterest.map(
                                (account, index) => (
                                  <PinterestIcon key={index} />
                                )
                              )}
                            </Stack>
                          )}

                        {socialAccounts?.x &&
                          Array.isArray(socialAccounts.x) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.x.map((account, index) => (
                                <XIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.behance &&
                          Array.isArray(socialAccounts.behance) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.behance.map((account, index) => (
                                <BehanceIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.tikTok &&
                          Array.isArray(socialAccounts.tikTok) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.tikTok.map((account, index) => (
                                <TikTokIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.snapchat &&
                          Array.isArray(socialAccounts.snapchat) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.snapchat.map((account, index) => (
                                <SnapchatIcon key={index} />
                              ))}
                            </Stack>
                          )}
                      </Stack>
                    </Box>
                  )}

                  {(socialAccounts?.spotify ||
                    socialAccounts?.apple ||
                    socialAccounts?.anghami) && (
                    <Box>
                      <Typography
                        component="h2"
                        variant="subtitle1"
                        sx={{ color: "#747474" }}
                        mb={1}
                      >
                        {translation("musicAppTitle")}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ gap: "8px", "& .MuiStack-root": { margin: 0 } }}
                      >
                        {socialAccounts?.spotify &&
                          Array.isArray(socialAccounts.spotify) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.spotify.map((account, index) => (
                                <SpotifyIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.apple &&
                          Array.isArray(socialAccounts.apple) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.apple.map((account, index) => (
                                <AppleIcon key={index} />
                              ))}
                            </Stack>
                          )}

                        {socialAccounts?.anghami &&
                          Array.isArray(socialAccounts.anghami) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {socialAccounts.anghami.map((account, index) => (
                                <AnghamiIcon key={index} />
                              ))}
                            </Stack>
                          )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Card>
            </Grid>
          )}

          {documents && documents.length > 0 && (
            <Grid item xs={12}>
              <Card fullWidth style={{ padding: "16px" }}>
                <Stack direction="row" alignItems="center" gap={1} mb={2}>
                  <span
                    className="arrowIcon"
                    style={{ flexShrink: 0, width: "20px", height: "20px" }}
                  >
                    {">"}
                  </span>

                  <Typography fontSize={18}>
                    {translation("documentsTitle")}
                  </Typography>
                </Stack>

                <Stack gap={1}>
                  {/* Mobile */}
                  {documents.map((doc, index) => (
                    <img
                      key={index}
                      src={doc}
                      alt="document"
                      sx={{ objectFit: "cover" }}
                      width="100%"
                    />
                  ))}
                </Stack>
              </Card>
            </Grid>
          )}
        </Grid>
      </div>
    );

  return (
    <div
      className="profile-phone-preview"
      style={{
        backgroundImage: `url(${iphoneFrame})`,
        direction: language && language === "ar" ? "rtl" : "ltr",
      }}
    >
      <div className="profile-info">
        <div
          style={{
            backgroundColor: profileThemeColors[theme]?.primary,
          }}
        >
          <div className="profile-info-container">
            <div className="profile-info-head">
              <img src={logo} alt="Logo" width={32} height="auto" />

              {(profilePhoto?.image || profilePhoto) && (
                <img
                  src={profilePhoto?.image ? profilePhoto.image : profilePhoto}
                  alt="Profile"
                  width={64}
                  height={64}
                  className="profile-info-head-avatar"
                />
              )}

              <span className="profile-info-head-card">
                {translation("button")}
              </span>
            </div>

            {/* Name Field */}
            {name && (
              <p className="profile-info-name">
                <b>{name}</b>
              </p>
            )}

            {/* Position & Company Fields */}
            {(position || company) && (
              <p className="profile-info-position">
                {position && !company && position}
                {!position && company && company}
                {position &&
                  company &&
                  `${position} ${translation("at")} ${company}`}
              </p>
            )}

            {/* Industry field */}
            {industry && (
              <p className="profile-info-position">{translation(industry)}</p>
            )}

            {/* Email field */}
            {email && (
              <div className="profile-info-mail-wrap">
                <i>
                  <MailIcon size={12} color="white" />
                </i>

                <span className="profile-info-mail">{email}</span>
              </div>
            )}

            {/* Secondary email field */}
            {secondaryEmail && (
              <div className="profile-info-mail-wrap">
                <i>
                  <MailIcon size={12} color="white" />
                </i>

                <span className="profile-info-mail">{secondaryEmail}</span>
              </div>
            )}

            {/* Phone field */}
            {mobile && (
              <div className="profile-info-phone-wrap">
                <i>
                  <PhoneIcon size={12} color="white" />
                </i>

                <span>{mobile}</span>
              </div>
            )}

            {/* Secondary Mobile field */}
            {secondaryMobile && (
              <div className="profile-info-phone-wrap">
                <i>
                  <PhoneIcon size={12} color="white" />
                </i>

                <span>{secondaryMobile}</span>
              </div>
            )}

            {/* Landline number field */}
            {landlineNumber && (
              <div className="profile-info-phone-wrap">
                <i>
                  <PhoneIcon size={12} color="white" />
                </i>

                <span>
                  {landlineNumber}
                  {landlineExtension &&
                    ` ${translation(
                      "landlineExtensionText"
                    )} ${landlineExtension}`}
                </span>
              </div>
            )}

            {/* Address */}
            {address && <p className="profile-info-bio">{address}</p>}

            {/* Bio field */}
            {bio && <p className="profile-info-bio">{bio}</p>}
          </div>

          {/* Social icons */}
          <div className="social-container">
            {(socialAccounts?.whatsapp || socialAccounts?.link) && (
              <Box className="social-row">
                {socialAccounts?.whatsapp && (
                  <Social
                    Icon={WhatsappIcon}
                    title={translation("whatsapp")}
                    theme={theme}
                    number={socialAccounts.whatsapp.length}
                  />
                )}

                {socialAccounts?.link && (
                  <Social
                    Icon={LinkIcon}
                    title={translation("link")}
                    theme={theme}
                    number={socialAccounts.link.length}
                  />
                )}
              </Box>
            )}

            {(socialAccounts?.facebook ||
              socialAccounts?.instagram ||
              socialAccounts?.linkedin ||
              socialAccounts?.pinterest ||
              socialAccounts?.x ||
              socialAccounts?.behance ||
              socialAccounts?.tikTok ||
              socialAccounts?.snapchat) && (
              <Box>
                <Box paddingX="12px" marginY={2}>
                  <Typography color={profileThemeColors[theme].secondary}>
                    {translation("socialMediaTitle")}
                  </Typography>
                </Box>

                <Box className="social-row" marginBottom="12px">
                  {socialAccounts?.facebook && (
                    <Social
                      Icon={FacebookIcon}
                      title={translation("facebook")}
                      theme={theme}
                      number={socialAccounts.facebook.length}
                    />
                  )}

                  {socialAccounts?.instagram && (
                    <Social
                      Icon={InstagramIcon}
                      title={translation("instagram")}
                      theme={theme}
                      number={socialAccounts.instagram.length}
                    />
                  )}

                  {socialAccounts?.linkedin && (
                    <Social
                      Icon={LinkedinIcon}
                      title={translation("linkedin")}
                      theme={theme}
                      number={socialAccounts.linkedin.length}
                    />
                  )}

                  {socialAccounts?.pinterest && (
                    <Social
                      Icon={PinterestIcon}
                      title={translation("pinterest")}
                      theme={theme}
                      number={socialAccounts.pinterest.length}
                    />
                  )}

                  {socialAccounts?.x && (
                    <Social
                      Icon={XIcon}
                      title={translation("x")}
                      theme={theme}
                      number={socialAccounts.x.length}
                    />
                  )}

                  {socialAccounts?.behance && (
                    <Social
                      Icon={BehanceIcon}
                      title={translation("behance")}
                      theme={theme}
                      number={socialAccounts.behance.length}
                    />
                  )}

                  {socialAccounts?.tikTok && (
                    <Social
                      Icon={TikTokIcon}
                      title={translation("tikTok")}
                      theme={theme}
                      number={socialAccounts.tikTok.length}
                    />
                  )}

                  {socialAccounts?.snapchat && (
                    <Social
                      Icon={SnapchatIcon}
                      title={translation("snapchat")}
                      theme={theme}
                      number={socialAccounts.snapchat.length}
                    />
                  )}
                </Box>
              </Box>
            )}

            {(socialAccounts?.spotify ||
              socialAccounts?.anghami ||
              socialAccounts?.apple) && (
              <Box>
                <Box paddingX="12px" marginY={2}>
                  <Typography color={profileThemeColors[theme].secondary}>
                    {translation("musicAppTitle")}
                  </Typography>
                </Box>

                <Box className="social-row" marginBottom="12px">
                  {socialAccounts?.spotify && (
                    <Social
                      Icon={SpotifyIcon}
                      title={translation("spotify")}
                      theme={theme}
                      number={socialAccounts.spotify.length}
                    />
                  )}

                  {socialAccounts?.anghami && (
                    <Social
                      Icon={AnghamiIcon}
                      title={translation("anghami")}
                      theme={theme}
                      number={socialAccounts.anghami.length}
                    />
                  )}

                  {socialAccounts?.apple && (
                    <Social
                      Icon={AppleIcon}
                      title={translation("apple")}
                      theme={theme}
                      number={socialAccounts.apple.length}
                    />
                  )}
                </Box>
              </Box>
            )}
          </div>

          {/* Documents */}
          {documents && documents.length > 0 && (
            <div className="social-container">
              <Box paddingX="12px">
                <Typography color={profileThemeColors[theme].secondary}>
                  {translation("documentsTitle")}
                </Typography>
              </Box>

              <Box paddingX="16px" mt={1}>
                {documents.map((doc, index) => (
                  <img
                    key={index}
                    src={doc}
                    alt="document"
                    sx={{ objectFit: "cover" }}
                    width="100%"
                  />
                ))}
              </Box>
            </div>
          )}

          {/* Footer */}
          <div className="profile-phone-foot">
            <div>
              <span>https://netifyhub.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePhonePreview;
